/**
 * Palantir PMS - Software para la gestión de la industria hotelera
 * Palantir PMS - Software for hotel industry management
 * Autor / Author: Alberto Castro Ull
 * Fecha de creación / Created on: 31/07/2025
 * Todos los derechos reservados / All rights reserved.
 * Este software es propiedad de Alberto Castro Ull.
 * This software is the property of Alberto Castro Ull.
 * Su uso está restringido a clientes con licencia emitida por Markulia Group, S.L.
 * Use is restricted to clients licensed by Markulia Group, S.L.
 */

import React, { useState, useEffect } from "react";
import axios from "../api/axiosInstance"; // token incluido automáticamente
import { useNavigate } from "react-router-dom";
import CustomerForm from "../Components/CustomerForm";

const NewCustomer = () => {
  const [formData, setFormData] = useState({
    salutation: "",
    first_name: "",
    middle_name: "",
    last_name: "",
    second_last_name: "",
    gender: "",
    birth_date: "",
    email: "",
    phone: "",
    country: "",
    city: "",
    address: "",
    postal_code: "",
    document_type: "",
    document_number: "",
    is_vip: false,
    vip_level: "",
    customer_description: "",
    photo_url: "",
    status: "active",
  });

  

  const navigate = useNavigate();

  useEffect(() => {
    document.body.classList.add("check-in-scroll");

    return () => {
        document.body.classList.remove("check-in-scroll");
    };
}, []);

const [maxAgeChild, setMaxAgeChild] = useState(null);
const [maxAgeBaby, setMaxAgeBaby] = useState(null);

useEffect(() => {
  const params = new URLSearchParams(window.location.search);
  const businessUnitId = params.get("business_unit_id") || "1";

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/hotel-settings`, {
        params: { business_unit_id: businessUnitId }
      });
      setMaxAgeChild(res.data.max_age_child);
      setMaxAgeBaby(res.data.max_age_baby);
    } catch (err) {
      console.error("❌ Error fetching hotel settings:", err);
    }
  };

  fetchSettings();
}, []);

useEffect(() => {
  if (!formData.birth_date || maxAgeChild === null || maxAgeBaby === null) return;

  const birthDate = new Date(formData.birth_date);
  const today = new Date();

  let age = today.getFullYear() - birthDate.getFullYear();
  const m = today.getMonth() - birthDate.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }

  const minAdultAge = parseInt(maxAgeChild, 10) + 1;

  if (age < minAdultAge) {
    alert(`❌ This customer is too young to be main guest.\nCurrent age: ${age} years`);
    setFormData((prev) => ({ ...prev, birth_date: "" }));
  }
}, [formData.birth_date, maxAgeChild, maxAgeBaby]);


  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Si document_type es un string vacío, lo cambiamos a null
    const updatedFormData = {
      ...formData,
      document_type: formData.document_type === "" ? null : formData.document_type,
      document_number: formData.document_number === "" ? null : formData.document_number,
      salutation: formData.salutation === "" ? null : formData.salutation,
      gender: formData.gender === "" ? null : formData.gender,
      vip_level: formData.vip_level === "" ? null : formData.vip_level,
      birth_date: isNaN(new Date(formData.birth_date).getTime()) ? null : formData.birth_date
    };
    

    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/customers`, updatedFormData);
      const newCustomerId = response.data.id;

      // Obtener parámetros de la URL
      const params = new URLSearchParams(window.location.search);
      const businessUnitId = params.get("business_unit_id");
      const fromReservation = params.get("fromReservation"); // Verificar si viene de reserva

      if (!businessUnitId) {
        alert("Error: business_unit_id no encontrado. Verifica la URL.");
        return;
      }

      if (fromReservation) {
        // Redirigir directamente a la reserva
        navigate(`/new-reservation?customer_id=${newCustomerId}&business_unit_id=${businessUnitId}`);
      } else {
        // Redirigir a selección de cliente
        navigate(`/select-customer?business_unit_id=${businessUnitId}`);
      }
    } catch (error) {
      console.error("Error creating customer:", error);
      alert("There was a problem creating the customer");
    }
  };

  return (
    <div className="p-6 h-[80vh] overflow-hidden flex flex-col">
  
  {/* Botón Back to Add New Reservation */}{/* Botón Back to Add New Reservation */}
  <div className="d-flex justify-content-start mb-1">
  <button 
    type="button" 
    onClick={() => {
      const params = new URLSearchParams(window.location.search);
      const businessUnitId = params.get("business_unit_id") || "1";
      navigate(`/new-reservation?business_unit_id=${businessUnitId}`);
    }}
    style={{
      fontSize: "0.85rem",
      padding: "4px 10px",
      backgroundColor: "#cfa31f",
      border: "none",
      borderRadius: "4px",
      fontWeight: "bold"
    }}
  >
    ← Back to Add New Reservation
  </button>
</div>



  <div className="d-flex justify-content-center">
    <form 
      onSubmit={handleSubmit} 
      className="container fs-6"
      style={{ transform: "scale(0.75)", transformOrigin: "top center", width: "120%" }}
    >
      <div className="card bg-dark text-light p-4">
        <h2 className="fs-5 text-center mb-3 fw-bold" style={{ color: "#FFD700" }}>
          Create New Customer
        </h2>

        <CustomerForm
          formData={formData}
          handleChange={handleChange}
          handleSelectChange={(selected, action) => {
            const name = action.name;
            const value = selected ? selected.value : "";
          
            setFormData((prev) => ({
              ...prev,
              [name]: value
            }));
          }}
          
          isVipEnabled={true}
          isUrlEnabled={true}
          isDescriptionEnabled={true}
          isVipRequired={true}
        />

          

        <button 
          type="submit" 
          className="btn text-white fw-bold border-0 w-100 mt-3"
          style={{ 
            background: "linear-gradient(to right, #9b59b6, #8e44ad, #5B2C6F)", 
            borderRadius: "5px", 
            padding: "10px 20px"
          }}
        >
          Save Customer
        </button>
        </div>
      </form>
    </div>
  </div>
  );
};

export default NewCustomer;

